import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Button from '@mui/material/Button';
import axios from 'axios';
import { redirect } from 'next/dist/server/api-utils';
import Image from 'next/image';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import process from 'process';
import React, { useState } from 'react';

interface SignInOrRegisterModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const SignInOrRegisterModal: React.FC<SignInOrRegisterModalProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [isRegistered, setIsRegistered] = useState(false); // true means login form, false means registration form
  const [errorMessage, setErrorMessage] = useState('');
  const [isEmailEntered, setIsEmailEntered] = useState(false);

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.get(
        `${process.env.NEXT_PUBLIC_API_URL}/api/v1/auth/check-user-exists/${emailOrPhone}`,
      );
      if (response.data.exists) {
        // User exists, proceed to login
        // Implement your login logic here
        setErrorMessage('');
      } else {
        // User does not exist, show registration form
        setIsRegistered(false);
        setErrorMessage('');
      }
    } catch (error) {
      console.log('error : ', error);
      setErrorMessage('An error occurred. Please try again.');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const password = (e.target as any).password.value;
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/api/v1/auth/register-with-email`,
        {
          email: emailOrPhone,
          password,
        },
      );
      // Implement your registration logic here
      setErrorMessage('');
      setIsOpen(false); // Close modal after registration
      if (response.data.token) {
        await signIn('credentials', {
          identifier: emailOrPhone,
          password,
          callbackUrl: `${process.env.NEXT_PUBLIC_HOST_URL}`, // or any URL you want to redirect to after login
        });
      }
      console.log('Registration successful', response.data);
    } catch (error) {
      setErrorMessage('An error occurred. Please try again.');
    }
  };

  const handleLoginWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const password = (e.target as any).password.value;
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/api/v1/auth/login-with-email`,
        {
          email: emailOrPhone,
          password,
        },
      );
      // Implement your registration logic here
      setErrorMessage('');
      setIsOpen(false); // Close modal after registration
      if (response.data.token) {
        await signIn('credentials', {
          identifier: emailOrPhone,
          password,
          callbackUrl: `${process.env.NEXT_PUBLIC_HOST_URL}`, // or any URL you want to redirect to after login
        });
      }
      console.log('Registration successful', response.data);
    } catch (error) {
      setErrorMessage('An error occurred. Please try again.');
    }
  };

  const handleSignInWithGoogle = async () => {
    try {
      const result = await signIn('google', {
        callbackUrl: '/dashboard',
        redirect: false,
      });
      
      if (result?.error) {
        console.error('Google sign in error:', result.error);
        setErrorMessage('Google登入失敗，請稍後再試');
      } else if (result?.ok) {
        // Close modal and redirect will be handled by NextAuth
        setIsOpen(false);
        // Force a page refresh to ensure session is properly loaded
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Google sign in error:', error);
      setErrorMessage('Google登入失敗，請稍後再試');
    }
  };

  return (
    <div
      id="authentication-modal"
      className={`${
        isOpen ? '' : 'hidden'
      } fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden`}
    >
      <div className="relative w-full max-w-md p-4">
        <div className="relative rounded-lg bg-white shadow dark:bg-gray-700">
          <div className="flex items-center justify-between rounded-t border-b p-4 dark:border-gray-600 md:p-5">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              登入 / 註冊
            </h3>

            <button
              type="button"
              className="ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
              onClick={toggleModal}
            >
              <svg
                className="h-3 w-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          <div className="p-4 md:p-5">
            {!isEmailEntered ? (
              <form className="space-y-4" onSubmit={handleContinue}>
                <div>
                  <h2 className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    電郵
                  </h2>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={emailOrPhone}
                    onChange={(e) => setEmailOrPhone(e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600 dark:text-white dark:placeholder:text-gray-400"
                    placeholder="example@email.com"
                    required
                  />
                </div>
                {errorMessage && <p className="text-red-500">{errorMessage}</p>}
                <button
                  type="submit"
                  className="w-full rounded-lg bg-blue-700 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                >
                  下一步
                </button>
                {/* <div className="flex">
                      <span className="absolute left-1/2 -translate-x-1/2 bg-white px-3 font-medium text-gray-900 dark:bg-gray-900 dark:text-white">
                        More Sign In Options
                      </span>
                    </div> */}
                <ul className="my-4 space-y-3">
                  <li className="group flex items-center rounded-lg bg-gray-50 p-3 text-base font-bold text-gray-900 hover:bg-gray-100 hover:shadow dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500">
                    <button
                      type="button"
                      onClick={() => {
                        handleSignInWithGoogle();
                      }}
                    >
                      <div className="flex flex-row gap-3">
                        <Image
                          alt="google"
                          width={24}
                          height={24}
                          src="/assets/svg/google-icon.svg"
                        />
                        以Google登入
                      </div>
                    </button>
                  </li>
                </ul>
              </form>
            ) : (
              <div>
                {isRegistered ? (
                  <form className="space-y-4" onSubmit={handleLoginWithEmail}>
                    <div>
                      <h2 className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                        Email: {emailOrPhone}
                      </h2>
                      <h2 className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                        Enter your password
                      </h2>
                      <input
                        type="password"
                        name="password"
                        id="password"
                        className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600 dark:text-white dark:placeholder:text-gray-400"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                    {errorMessage && (
                      <p className="text-red-500">{errorMessage}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full rounded-lg bg-blue-700 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                    >
                      Sign In
                    </button>
                  </form>
                ) : (
                  <form className="space-y-4" onSubmit={handleRegister}>
                    <div>
                      <h2 className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                        Set your password
                      </h2>
                      <p>
                        We recommend that you set a strong password for your
                        account:
                      </p>
                      <input
                        type="password"
                        name="password"
                        id="password"
                        className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600 dark:text-white dark:placeholder:text-gray-400"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                    {errorMessage && (
                      <p className="text-red-500">{errorMessage}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full rounded-lg bg-blue-700 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                    >
                      Register and Sign In
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInOrRegisterModal;
